import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useField } from 'formik';
import styled from 'styled-components';

import * as graphql from 'generated/graphql';

const Container = styled(Flex)<{ party: graphql.EvaluationSubmissionParty }>`
	position: relative;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	color: ${(props) => props.theme.colors.slateBlue};
	border-radius: 5px;
	gap: 10px;
`;

const ProgressBar = styled(Box)`
	height: 6px;
	border-radius: 3px;
	width: 100px;
	background: rgba(0, 0, 0, 0.25);
`;

const ProgressBarFill = styled(Box)<{ party: graphql.EvaluationSubmissionParty; fraction: number }>`
	background: black;
	height: 6px;
	border-radius: 3px;
	width: ${(props) => (props.fraction * 100).toFixed() + '%'};
`;

export default function EvaluationSubmissionProgress(props: {
	submission: graphql.EvaluationSubmission;
}) {
	const { t } = useTranslation();

	const [{ value: sections }] = useField('sections');

	const ignored = [
		graphql.EvaluationSectionItemType.Title,
		graphql.EvaluationSectionItemType.Paragraph,
		graphql.EvaluationSectionItemType.FinalScore,
		graphql.EvaluationSectionItemType.Signature,
	];

	const indices = props.submission.evaluationForm.sections
		.flatMap((section, sectionIndex) =>
			section.items.map((item, itemIndex) => ({
				sectionIndex,
				itemIndex,
				type: item.type,
				fillInBy: item.fillInBy,
			})),
		)
		.filter(({ type }) => !ignored.includes(type))
		.filter(({ fillInBy }) => {
			if (props.submission.viewer.party === graphql.EvaluationSubmissionParty.Employee) {
				return [graphql.EvaluationFillInBy.Employee, graphql.EvaluationFillInBy.Both].includes(
					fillInBy,
				);
			}

			return [graphql.EvaluationFillInBy.Manager, graphql.EvaluationFillInBy.Both].includes(
				fillInBy,
			);
		});

	const filledIn = indices.filter(({ sectionIndex, itemIndex }) => {
		const item = sections[sectionIndex].items[itemIndex];

		return (
			item?.openQuestion?.ops.length > 0 ||
			(item?.competency ?? undefined) !== undefined ||
			(item?.sentiment ?? undefined) !== undefined ||
			(item?.stars ?? undefined) !== undefined
		);
	}).length;

	let status = props.submission.viewer.hasShared ? 'shared' : 'concept';

	if (!props.submission.viewer.requiresInput) {
		if (props.submission.employee.requiresInput && !props.submission.employee.hasShared) {
			status = 'self';
		}

		if (props.submission.manager.requiresInput && !props.submission.manager.hasShared) {
			status = 'self';
		}
	}

	const party = props.submission.viewer.party;

	return (
		<Container
			party={party}
			alignItems={['left', 'center']}
			px={4}
			py={3}
			flexDirection={['column', 'row']}
		>
			<Box flexGrow={1} flexShrink={1}>
				{t(`evaluation.statusBar.${status}.${party.toLowerCase()}`, {
					employee:
						props.submission.employee.user.firstName +
						' ' +
						props.submission.employee.user.lastName,
					manager:
						props.submission.manager.user.firstName + ' ' + props.submission.manager.user.lastName,
				})}
			</Box>
			<Box mr={3} flexGrow={0} flexShrink={0}>
				<ProgressBar>
					<ProgressBarFill party={party} fraction={filledIn / indices.length} />
				</ProgressBar>
			</Box>
			<Box mr={3} flexGrow={0} flexShrink={0}>
				{filledIn === indices.length
					? props.submission.viewer.viewerCanSign
						? t('evaluations.statusBar.confirmWhenReady')
						: t('evaluations.statusBar.completedAllQuestions')
					: t('evaluation.statusBar.completedQuestions', {
							completed: filledIn,
							total: indices.length,
					  })}
			</Box>
		</Container>
	);
}
